// src/store/userStore.ts
import { create } from "zustand";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { UserDatabaseStructure } from "../services/database/userStructure";
import { useDebugStore } from "./debugStore";
import { UniversalDirectoryManager } from "../services/storage/universalDirectoryManager";
import { useVCardStore } from "./vcardStore";
import type { UserProfile, MembershipTier } from "../types/user";

interface UserState {
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  setProfile: (profile: UserProfile | null) => void; // Sets the current user profile in the store.
  createProfile: (
    userId: string,
    email: string,
    firstName: string,
    lastName: string,
    isAdmin?: boolean,
  ) => Promise<UserProfile>; // Creates or updates a user profile.
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>; // Updates specific fields in the user's profile.
  upgradeMembership: (tier: MembershipTier) => Promise<void>; // Upgrades the user's membership tier and features.
  cancelMembership: () => Promise<void>; // Cancels the user's membership auto-renewal.
}

type MembershipFeatures = UserProfile["membership"]["features"];

export const useUserStore = create<UserState>((set, get) => ({
  profile: null,
  loading: false,
  error: null,

  // Sets the user profile in the store.
  setProfile: (profile) => set({ profile }),

  /**
   * Creates a new user profile or updates an existing one.
   * Adds a `name` catchall field derived from `firstName` and `lastName`.
   */
  createProfile: async (
    userId: string,
    email: string,
    firstName: string,
    lastName: string,
    isAdmin?: boolean,
  ) => {
    set({ loading: true, error: null });
    try {
      // Ensure firstName and lastName are valid strings
      firstName = firstName?.trim() || "Unknown";
      lastName = lastName?.trim() || "User";
      const fullName = `${firstName} ${lastName}`.trim(); // Generate fullName

      // First, ensure the user structure exists
      const structureResult = await UserDatabaseStructure.ensureUserStructure(
        userId,
        email,
        fullName,
      );
      if (!structureResult.success) {
        throw new Error(
          `Failed to initialize user structure: ${structureResult.error}`,
        );
      }

      const now = serverTimestamp();

      const newProfile: UserProfile = {
        id: userId,
        email,
        firstName,
        lastName,
        fullName,
        name: fullName, // Catchall for backward compatibility
        role: isAdmin ? "admin" : "user",
        membership: {
          tier: "free",
          startDate: now,
          autoRenew: false,
          features: {
            maxVCards: 3,
            maxDevices: 3,
            customBranding: false,
            analytics: false,
            bulkGeneration: false,
          },
        },
        createdAt: now,
        updatedAt: now,
      };

      const profileRef = doc(
        db,
        UserDatabaseStructure.getProfilePath(userId),
        "data",
      );

      // Check if the document already exists
      const docSnap = await getDoc(profileRef);
      if (docSnap.exists()) {
        // Preserve role/roles/membership for existing users.
        const existingProfile = docSnap.data() as Partial<UserProfile>;
        const profileUpdates: Partial<UserProfile> = {
          email,
          firstName,
          lastName,
          fullName,
          name: fullName,
          updatedAt: now,
        };

        await setDoc(profileRef, profileUpdates, { merge: true });

        const mergedProfile = {
          ...existingProfile,
          ...profileUpdates,
          id: userId,
          role: existingProfile.role ?? newProfile.role,
          roles: Array.isArray(existingProfile.roles)
            ? existingProfile.roles
            : undefined,
          membership: existingProfile.membership ?? newProfile.membership,
          createdAt: existingProfile.createdAt ?? newProfile.createdAt,
          updatedAt: now,
        } as UserProfile;

        set({ profile: mergedProfile, loading: false });
        return mergedProfile;
      } else {
        // Create a new profile document
        await setDoc(profileRef, newProfile);
      }

      set({ profile: newProfile, loading: false });
      return newProfile;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create profile";
      useDebugStore
        .getState()
        .addMessage("error", `Error in createProfile: ${errorMessage}`);
      set({ error: errorMessage, loading: false });
      throw new Error(`[createProfile]: ${errorMessage}`);
    }
  },

  /**
   * Updates specific fields in the user's profile.
   * Automatically updates the `fullName` field if `firstName` or `lastName` changes.
   */
  updateProfile: async (updates: Partial<UserProfile>) => {
    const { profile } = get();
    const addDebugMessage = useDebugStore.getState().addMessage;
    if (!profile) {
      addDebugMessage("error", "Cannot update profile: no profile found");
      throw new Error("No profile to update");
    }

    set({ loading: true, error: null });

    try {
      const now = serverTimestamp();
      const profileRef = doc(
        db,
        UserDatabaseStructure.getProfilePath(profile.id),
        "data",
      );

      // Auto-update fullName if firstName or lastName is modified
      if (updates.firstName || updates.lastName) {
        updates.fullName = `${updates.firstName || profile.firstName} ${
          updates.lastName || profile.lastName
        }`.trim();
        updates.name = updates.fullName;
      }

      // Always include both timestamps for consistency with VCard
      const updateData = {
        ...updates,
        updatedAt: now,
        lastModified: now, // Add lastModified to match VCard
      };
      const hasLinkedInUpdate =
        updates.socialMedia !== undefined &&
        Object.prototype.hasOwnProperty.call(updates.socialMedia, "linkedin");

      // Update the document (don't use merge to ensure timestamps are updated)
      await updateDoc(profileRef, updateData);

      // Update local state with the same timestamps
      set({
        profile: {
          ...profile,
          ...updates,
          updatedAt: now,
          lastModified: now,
        },
        loading: false,
      });

      addDebugMessage("success", "Profile updated successfully");

      // Sync name + email fields to all vCards (test scope)
      const shouldSyncOwnerFields =
        updates.firstName !== undefined ||
        updates.lastName !== undefined ||
        updates.email !== undefined ||
        updates.photoUrl !== undefined ||
        updates.alternativeEmail !== undefined ||
        updates.phone !== undefined ||
        updates.travelPhone !== undefined ||
        updates.workPhone !== undefined ||
        updates.messagingContact !== undefined ||
        updates.messagingContacts !== undefined ||
        updates.address !== undefined ||
        updates.emergencyContact !== undefined ||
        updates.medicalInfo !== undefined ||
        hasLinkedInUpdate;

      if (shouldSyncOwnerFields) {
        const nextFirstName = updates.firstName ?? profile.firstName;
        const nextLastName = updates.lastName ?? profile.lastName;
        const nextEmail = updates.email ?? profile.email;
        const nextAlternativeEmail =
          updates.alternativeEmail ?? profile.alternativeEmail;
        const nextPhone = updates.phone ?? profile.phone;
        const nextTravelPhone = updates.travelPhone ?? profile.travelPhone;
        const nextWorkPhone = updates.workPhone ?? profile.workPhone;
        const nextMessagingContacts =
          updates.messagingContacts ??
          (Array.isArray(profile.messagingContacts)
            ? profile.messagingContacts
            : undefined);
        const nextMessagingContact =
          updates.messagingContact !== undefined
            ? updates.messagingContact
            : updates.messagingContacts !== undefined
              ? (nextMessagingContacts?.[0] ?? null)
              : Array.isArray(nextMessagingContacts)
                ? (nextMessagingContacts[0] ?? null)
                : (profile.messagingContact ?? null);
        const nextAddress = updates.address ?? profile.address;
        const nextEmergencyContact =
          updates.emergencyContact ?? profile.emergencyContact;
        const nextMedicalInfo = updates.medicalInfo ?? profile.medicalInfo;
        const nextLinkedIn = hasLinkedInUpdate
          ? (updates.socialMedia?.linkedin ?? "")
          : (profile.socialMedia?.linkedin ?? "");
        const vcardUpdates: Record<string, unknown> = {};
        const businessLinkedInUpdate = hasLinkedInUpdate
          ? nextLinkedIn
          : undefined;
        const emergencyContactUpdate = nextEmergencyContact
          ? {
              name: nextEmergencyContact.name ?? "",
              phone: nextEmergencyContact.phone ?? "",
              relationship: nextEmergencyContact.relationship ?? "",
            }
          : undefined;
        const medicalInfoUpdate = nextMedicalInfo
          ? {
              bloodType: nextMedicalInfo.bloodType ?? "",
              allergies: nextMedicalInfo.allergies ?? "",
              conditions: nextMedicalInfo.conditions ?? "",
              medications: nextMedicalInfo.medications ?? "",
              healthFund: nextMedicalInfo.healthFund ?? "",
              memberNumber: nextMedicalInfo.memberNumber ?? "",
              healthFundId: nextMedicalInfo.healthFundId ?? "",
              cardNumber: nextMedicalInfo.cardNumber ?? "",
              medicareNumber: nextMedicalInfo.medicareNumber ?? "",
              medicareIrn: nextMedicalInfo.medicareIrn ?? "",
              medicareExpiry: nextMedicalInfo.medicareExpiry ?? "",
              concessionNumberExpiry:
                nextMedicalInfo.concessionNumberExpiry ?? "",
              ndisNumber: nextMedicalInfo.ndisNumber ?? "",
            }
          : undefined;

        if (nextFirstName !== undefined) {
          vcardUpdates.firstName = nextFirstName;
        }

        if (nextLastName !== undefined) {
          vcardUpdates.lastName = nextLastName;
        }

        const shouldSyncFormattedName =
          updates.firstName !== undefined || updates.lastName !== undefined;
        if (shouldSyncFormattedName) {
          const normalizedFirstName =
            typeof nextFirstName === "string" ? nextFirstName.trim() : "";
          const normalizedLastName =
            typeof nextLastName === "string" ? nextLastName.trim() : "";
          vcardUpdates.formattedName =
            `${normalizedFirstName} ${normalizedLastName}`.trim();
        }

        if (updates.photoUrl !== undefined) {
          vcardUpdates.photo = updates.photoUrl;
        }

        if (nextEmail !== undefined) {
          vcardUpdates.email = nextEmail;
        }

        if (nextAlternativeEmail !== undefined) {
          vcardUpdates.workEmail = nextAlternativeEmail;
        }

        if (nextPhone !== undefined) {
          vcardUpdates.phone = nextPhone;
          vcardUpdates.mobilePhone = nextPhone;
        }

        if (nextTravelPhone !== undefined) {
          vcardUpdates.travelPhone = nextTravelPhone;
        }

        if (nextWorkPhone !== undefined) {
          vcardUpdates.workPhone = nextWorkPhone;
        }

        if (updates.messagingContacts !== undefined) {
          vcardUpdates.messagingContacts = nextMessagingContacts ?? [];
        }

        if (
          updates.messagingContact !== undefined ||
          updates.messagingContacts !== undefined
        ) {
          vcardUpdates.messagingContact = nextMessagingContact;
        }

        if (nextAddress !== undefined) {
          vcardUpdates.address = {
            street: nextAddress.street ?? "",
            unit: nextAddress.unit ?? "",
            city: nextAddress.city ?? "",
            state: nextAddress.state ?? "",
            zip: nextAddress.zip ?? "",
            country: nextAddress.country ?? "",
          };
        }

        const hasGlobalUpdates = Object.keys(vcardUpdates).length > 0;

        if (
          hasGlobalUpdates ||
          emergencyContactUpdate ||
          medicalInfoUpdate ||
          businessLinkedInUpdate !== undefined
        ) {
          const vcardsPath = UniversalDirectoryManager.getPath({
            userId: profile.id,
            contentType: "vcard",
          });
          const vcardsRef = collection(db, vcardsPath);
          const snapshot = await getDocs(vcardsRef);

          if (!snapshot.empty) {
            const batch = writeBatch(db);
            const vcardTimestamp = serverTimestamp();
            let updatedCount = 0;

            snapshot.docs.forEach((docSnap) => {
              const isExtremeKit = docSnap.id === "extremeKit";
              const isBusiness = docSnap.id === "business";
              if (
                !hasGlobalUpdates &&
                !(
                  isExtremeKit &&
                  (emergencyContactUpdate || medicalInfoUpdate)
                ) &&
                !(isBusiness && businessLinkedInUpdate !== undefined)
              ) {
                return;
              }

              const perDocUpdates: Record<string, unknown> = {
                ...(hasGlobalUpdates ? vcardUpdates : {}),
                updatedAt: vcardTimestamp,
                lastModified: vcardTimestamp,
              };

              if (emergencyContactUpdate && isExtremeKit) {
                perDocUpdates[
                  "categorySpecificInfo.extremeKit.emergencyContact"
                ] = emergencyContactUpdate;
              }

              if (medicalInfoUpdate && isExtremeKit) {
                perDocUpdates["categorySpecificInfo.extremeKit.medicalInfo"] =
                  medicalInfoUpdate;
              }

              if (businessLinkedInUpdate !== undefined && isBusiness) {
                perDocUpdates["categorySpecificInfo.business.linkedin"] =
                  businessLinkedInUpdate;
              }

              batch.update(docSnap.ref, {
                ...perDocUpdates,
              });
              updatedCount += 1;
            });

            await batch.commit();

            // Update local vcard store in-memory
            useVCardStore.setState((state) => ({
              vcards: state.vcards.map((vcard) => {
                if (vcard.userId !== profile.id) {
                  return vcard;
                }

                const updatedVCard = { ...vcard, ...vcardUpdates };
                const needsExtremeKitUpdate =
                  vcard.category === "extremeKit" &&
                  Boolean(emergencyContactUpdate || medicalInfoUpdate);
                const needsBusinessLinkedInUpdate =
                  vcard.category === "business" &&
                  businessLinkedInUpdate !== undefined;

                if (!needsExtremeKitUpdate && !needsBusinessLinkedInUpdate) {
                  return updatedVCard;
                }

                const existingCategoryInfo =
                  vcard.categorySpecificInfo &&
                  typeof vcard.categorySpecificInfo === "object"
                    ? (vcard.categorySpecificInfo as Record<string, unknown>)
                    : {};
                const existingExtremeKit =
                  typeof existingCategoryInfo.extremeKit === "object" &&
                  existingCategoryInfo.extremeKit !== null
                    ? (existingCategoryInfo.extremeKit as Record<
                        string,
                        unknown
                      >)
                    : {};
                const existingBusiness =
                  typeof existingCategoryInfo.business === "object" &&
                  existingCategoryInfo.business !== null
                    ? (existingCategoryInfo.business as Record<string, unknown>)
                    : {};

                return {
                  ...updatedVCard,
                  categorySpecificInfo: {
                    ...existingCategoryInfo,
                    ...(needsExtremeKitUpdate
                      ? {
                          extremeKit: {
                            ...existingExtremeKit,
                            ...(emergencyContactUpdate
                              ? { emergencyContact: emergencyContactUpdate }
                              : {}),
                            ...(medicalInfoUpdate
                              ? { medicalInfo: medicalInfoUpdate }
                              : {}),
                          },
                        }
                      : {}),
                    ...(needsBusinessLinkedInUpdate &&
                    businessLinkedInUpdate !== undefined
                      ? {
                          business: {
                            ...existingBusiness,
                            linkedin: businessLinkedInUpdate,
                          },
                        }
                      : {}),
                  },
                };
              }),
            }));

            addDebugMessage(
              "info",
              `Synced owner fields to ${updatedCount} vCards`,
            );
          }
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update profile";
      set({ error: errorMessage, loading: false });
      addDebugMessage("error", `Failed to update profile: ${errorMessage}`);
      throw new Error(`[updateProfile]: ${errorMessage}`);
    }
  },

  /**
   * Upgrades the user's membership tier and updates related features.
   */
  upgradeMembership: async (tier: MembershipTier) => {
    const { profile } = get();
    if (!profile) throw new Error("No profile to upgrade membership");

    set({ loading: true, error: null });
    try {
      const featureDefaults: MembershipFeatures = {
        maxVCards: 0,
        maxDevices: 0,
        customBranding: false,
        analytics: false,
        bulkGeneration: false,
      };

      const tierFeatures: Record<MembershipTier, MembershipFeatures> = {
        free: { ...featureDefaults, maxVCards: 3, maxDevices: 3 },
        premium: {
          ...featureDefaults,
          maxVCards: 10,
          maxDevices: 10,
          customBranding: true,
          analytics: true,
        },
        business: {
          ...featureDefaults,
          maxVCards: 100,
          maxDevices: 50,
          customBranding: true,
          analytics: true,
          bulkGeneration: true,
        },
        enterprise: {
          ...featureDefaults,
          maxVCards: -1,
          maxDevices: -1,
          customBranding: true,
          analytics: true,
          bulkGeneration: true,
        },
      };

      const features = tierFeatures[tier] ?? featureDefaults;

      const now = serverTimestamp();
      const localStartDate = new Date();
      const renewalDate = new Date(localStartDate);
      renewalDate.setMonth(renewalDate.getMonth() + 1);

      const membershipUpdate = {
        membership: {
          tier,
          startDate: now,
          endDate: renewalDate,
          autoRenew: true,
          features,
        },
        updatedAt: now,
      };

      const profileRef = doc(
        db,
        UserDatabaseStructure.getProfilePath(profile.id),
        "data",
      );
      await setDoc(profileRef, membershipUpdate, { merge: true });

      set({
        profile: {
          ...profile,
          membership: {
            tier,
            startDate: localStartDate,
            endDate: renewalDate,
            autoRenew: true,
            features,
          },
          updatedAt: now,
        },
        loading: false,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to upgrade membership";
      set({ error: errorMessage, loading: false });
      throw new Error(`[upgradeMembership]: ${errorMessage}`);
    }
  },

  /**
   * Cancels the user's membership auto-renewal.
   */
  cancelMembership: async () => {
    const { profile } = get();
    if (!profile) throw new Error("No profile to cancel membership for");

    set({ loading: true, error: null });
    try {
      const now = serverTimestamp();
      const profileRef = doc(
        db,
        UserDatabaseStructure.getProfilePath(profile.id),
        "data",
      );

      await updateDoc(profileRef, {
        "membership.autoRenew": false,
        updatedAt: now,
      });

      set({
        profile: {
          ...profile,
          membership: { ...profile.membership, autoRenew: false },
          updatedAt: now,
        },
        loading: false,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to cancel membership";
      set({ error: errorMessage, loading: false });
      throw new Error(`[cancelMembership]: ${errorMessage}`);
    }
  },
}));
